import React, { useState, useEffect } from 'react';

/**
 * Regex to extract identifiers.
 * * 1. EU Product Number: EU/1/ followed by two digits, then a slash, then three or four digits.
 * Pattern: EU\/1\/\d{2}\/\d{3,4}
 * * 2. EMA Number: EMEA/H/C/ followed by one to six digits.
 * Pattern: EMEA\/H\/C\/\d{1,6}
 * * The combined regex uses the OR operator (|) and the 'g' flag for global matching.
 */
const identifierRegex = /(EU\/1\/\d{2}\/\d{3,4})|(EMEA\/H\/C\/(\d{1,6}))/g;

/**
 * Component for pasting and processing EU/EMA identifiers.
 * @param {object} props
 * @param {object[]} allData - All available data to create the lookup map.
 * @param {function} updateSelection - Function to update the parent selection state.
 * @param {function} togglePasteInput - Function to toggle the visibility of this component.
 * @param {object} config - Application configuration object containing data keys.
 * @param {object[]} selectedData - The currently selected data objects in the current selection.
 * @param {object} tableUtils - The main table utils context.
 * @param {function} setTableUtils - The setter for the table utils context.
 */
function PasteIdInput({ allData, updateSelection, togglePasteInput, config, selectedData, tableUtils, setTableUtils }) {
  // State to hold the raw text from the paste identifier input
  const [pasteInput, setPasteInput] = useState('');
  // NEW: State to hold the debounced version of the input text
  const [debouncedInput, setDebouncedInput] = useState('');
  // State to manage the tooltip visibility
  const [showTooltip, setShowTooltip] = useState(false);

  // --- DEBOUNCE EFFECT ---
  // The delay in milliseconds for the debounce
  const DEBOUNCE_DELAY = 600; 

  useEffect(() => {
    // Set a timer to update the debounced input after the delay
    const handler = setTimeout(() => {
      setDebouncedInput(pasteInput);
    }, DEBOUNCE_DELAY);

    // Cleanup function to clear the previous timeout on every `pasteInput` change
    // This is the core of the debouncing mechanism
    return () => {
      clearTimeout(handler);
    };
  }, [pasteInput]); // Only re-run the effect if pasteInput changes
  // -------------------------

  /**
   * Creates an efficient lookup map for identifier conversion.
   * Key: ema_number or eu_pnumber, Value: eu_pnumber (the primary key).
   * NOTE: The map keys are the fully padded 6-digit EMA numbers.
   */
  const emaToEuPnumberMap = allData.reduce((map, item) => {
    // eu_pnumber is the primary key (PK)
    const euPnumber = item[config.data.primary_key];
    const emaNumber = item.ema_number; // This should be the fully padded number (e.g., EMEA/H/C/000073)

    if (euPnumber) {
        // Map the primary key to itself
        map[euPnumber] = euPnumber;
        // Map the secondary key (if it exists) to the primary key
        if (emaNumber) {
            map[emaNumber] = euPnumber;
        }
    }
    return map;
  }, {});

  /**
   * Function to extract all matching identifiers from the input text.
   * @param {string} text - The raw text from the paste input area.
   * @returns {string[]} An array of unique, extracted identifiers.
   */
  const extractIdentifiers = (text) => {
    // Check for text, otherwise matchAll will throw if text is null/undefined
    if (!text) return [];
    
    // Reset regex index before matchAll call (important for global regex in a loop/function)
    identifierRegex.lastIndex = 0; 

    const matches = [...text.matchAll(identifierRegex)];
    // MODIFIED: Instead of match[0], we need to process the matches to use the primary EMA format
    const extracted = matches
      .map(match => {
        // match[1] is the EU Product Number (if it exists)
        if (match[1]) {
          return match[1];
        }
        // match[2] is the full EMA Number (e.g., EMEA/H/C/73 or EMEA/H/C/000073)
        // match[3] is the captured digit sequence (e.g., '73' or '000073')
        if (match[2] && match[3]) {
          // Check if it's an EMA number (based on the presence of match[2] and match[3])
          const digits = match[3];
          // Pad the digits with leading zeros to exactly 6 characters
          const paddedDigits = digits.padStart(6, '0');
          // Recreate the EMA number with the full 6-digit format expected in the lookup map
          return `EMEA/H/C/${paddedDigits}`;
        }
        return null; // Should not happen if regex is correct, but safe
      })
      .filter(id => id); // Filter out any null values

    return [...new Set(extracted)];
  }

  /**
   * Processes the extracted identifiers: converts EMA numbers to EU Product Numbers
   * (primary keys) and returns a unique set of valid primary keys.
   * @param {string[]} identifiers - Array of extracted EU/EMA identifiers.
   * @returns {string[]} Array of unique EU Product Numbers (primary keys).
   */
  const processIdentifiers = (identifiers) => {
      const primaryKeys = new Set();

      identifiers.forEach(id => {
          // The 'id' here is now either an EU Product Number or a fully padded EMA Number
          const primaryKey = emaToEuPnumberMap[id];

          if (primaryKey) {
              primaryKeys.add(primaryKey);
          }
      });

      return [...primaryKeys];
  }

  // --- DERIVED/MEMOIZED VALUES (based on debouncedInput) ---
  const extractedRawIdentifiers = extractIdentifiers(debouncedInput);
  const processedPrimaryKeys = processIdentifiers(extractedRawIdentifiers);
  const isInputValid = debouncedInput.trim() && processedPrimaryKeys.length > 0;

  // Calculate Intersection
  const currentSelectionPrimaryKeys = new Set(
    selectedData.map(item => item[config.data.primary_key])
  );

  // This is the array of keys that are BOTH in the paste box AND already selected
  const alreadySelectedKeys = processedPrimaryKeys.filter(key => 
    currentSelectionPrimaryKeys.has(key)
  );
  const alreadySelectedCount = alreadySelectedKeys.length;
  
  // NEW LOGIC: Calculate the number of new medicines
  const newMedicineCount = processedPrimaryKeys.length - alreadySelectedCount;

  // Convert to a Set for efficient lookup in the effect
  const alreadySelectedKeysSet = new Set(alreadySelectedKeys);

  // --- HANDLERS (using the processed results) ---

  // Handler for 'Add to current selection' button
  const handleAddToSelection = () => {
    // Use the already processed primaryKeys
    if (processedPrimaryKeys.length > 0) {
        updateSelection(processedPrimaryKeys, 'add');
        setPasteInput(''); // Clear input after successful action
        setDebouncedInput(''); // Clear debounced input too
        togglePasteInput(); // Close the input after action
    }
  }

  // Handler for 'Make new selection' button
  const handleMakeNewSelection = () => {
    // Use the already processed primaryKeys
    if (processedPrimaryKeys.length > 0) {
        updateSelection(processedPrimaryKeys, 'new');
        setPasteInput(''); // Clear input after successful action
        setDebouncedInput(''); // Clear debounced input too
        togglePasteInput(); // Close the input after action
    }
  }

  // NEW: Effect to update the context with keys to highlight
  // This effect also runs based on the debounced keys
  useEffect(() => {
    if (setTableUtils) {
        // Check if the context already has the same keys to avoid unnecessary re-renders
        if (tableUtils?.highlightedPasteKeys?.size !== alreadySelectedKeysSet.size || 
            ![...alreadySelectedKeysSet].every(key => tableUtils.highlightedPasteKeys.has(key))) {
              
            setTableUtils(prevUtils => ({
                ...prevUtils,
                highlightedPasteKeys: alreadySelectedKeysSet // Store the Set of intersection keys
            }));
        }
    }
  }, [alreadySelectedKeysSet, setTableUtils, tableUtils?.highlightedPasteKeys]); // Re-run when the set of keys changes

  // NEW: Effect to clear the highlights when the component is unmounted (hidden)
  useEffect(() => {
    return () => {
      if (setTableUtils) {
        setTableUtils(prevUtils => ({
          ...prevUtils,
          highlightedPasteKeys: new Set() // Clear by setting an empty Set
        }));
      }
    };
  }, [setTableUtils]); // Run only on mount/unmount

  // Define the tooltip message
  const tooltipMessage = "Please paste and ensure at least one valid EU Product Number or EMA Number is identified.";

  return (
    <div className="selected-data-content">
        <hr className="med-top-separator" />
        {/* This button's onClick will unmount the component, triggering the cleanup effect */}
            <button
                className="med-primary-solid med-bx-button"
                onClick={togglePasteInput} 
            >
                <i className="bx bx-list-ul med-button-image"></i> Hide Quick Select
            </button>
        <h2 className="med-header">Quick Select - Using medicine IDs</h2>
        <div className="med-home-content">
        <p>Paste a table or list of EU Product Numbers (EU/1/XX/XXXX) and/or EMA Numbers (EMA/H/C/XXXXXX). The system will automatically extract all valid IDs and match them to medicines in the EMRD database.</p>
        <textarea
            id="med-paste-input"
            placeholder="e.g., EU/1/95/001, EU/1/24/1879, EMEA/H/C/006514, EMEA/H/C/6532, or EMEA/H/C/73"
            className="med-search-text-input med-text-input"
            style={{ width: '100%', minHeight: '150px', margin: '10px 0' }}
            value={pasteInput}
            onChange={(e) => setPasteInput(e.target.value)}
        />
        {/* The display text now shows results based on the debounced input */}
        {debouncedInput.trim() && (
          <p style={{ marginTop: '10px', textAlign: 'center' }}>
            Found {processedPrimaryKeys.length} matching EMRD {processedPrimaryKeys.length === 1 ? 'medicine' : 'medicines'}.
          </p>
        )}
        <div style={{ display: 'flex', gap: '10px', marginBottom: '10px', justifyContent: 'center' }}>

            {/* Wrapper for Tooltip: Add to Selection */}
            <div
                style={{ position: 'relative', display: 'inline-block' }}
                onMouseEnter={() => !isInputValid && setShowTooltip(true)}
                onMouseLeave={() => !isInputValid && setShowTooltip(false)}
            >
                <button
                    className="med-primary-solid med-bx-button"
                    onClick={handleAddToSelection}
                    disabled={!isInputValid}
                    style={!isInputValid ? { opacity: 0.6, cursor: 'not-allowed' } : {}}
                >
                    {/* MODIFIED BUTTON TEXT */}
                    <i className="bx bx-plus-circle med-button-image"></i> Add ({newMedicineCount}) to current selection
                </button>

                {/* Tooltip for Add to Selection */}
                {showTooltip && !isInputValid && (
                    <span
                        style={{
                            position: 'absolute',
                            bottom: '100%',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            padding: '5px 10px',
                            borderRadius: '4px',
                            backgroundColor: '#333',
                            color: 'white',
                            whiteSpace: 'nowrap',
                            zIndex: 10,
                            fontSize: '12px'
                        }}
                    >
                    </span>
                )}
            </div>

            {/* Wrapper for Tooltip: Make new selection */}
            <div
                style={{ position: 'relative', display: 'inline-block' }}
                onMouseEnter={() => !isInputValid && setShowTooltip(true)}
                onMouseLeave={() => !isInputValid && setShowTooltip(false)}
            >
                <button
                    className="med-primary-solid med-bx-button"
                    onClick={handleMakeNewSelection}
                    disabled={!isInputValid}
                    style={!isInputValid ? { opacity: 0.6, cursor: 'not-allowed' } : {}}
                >
                    <i className="bx bx-select-multiple med-button-image"></i> Make new selection ({processedPrimaryKeys.length})
                </button>

                {/* Tooltip for Make New Selection */}
                {showTooltip && !isInputValid && (
                    <span
                        style={{
                            position: 'absolute',
                            bottom: '100%',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            padding: '5px 10px',
                            borderRadius: '4px',
                            backgroundColor: '#333',
                            color: 'white',
                            whiteSpace: 'nowrap',
                            zIndex: 10,
                            fontSize: '12px'
                        }}
                    >
                    {tooltipMessage}
                    </span>
                )}
            </div>
            
            {/* Display the count of already selected medicines */}
            {alreadySelectedCount > 0 && (
                <span
                    style={{
                        padding: '5px 10px',
                        borderRadius: '4px',
                        backgroundColor: '#387c38', // A nice green color
                        color: 'white',
                        whiteSpace: 'nowrap',
                        fontSize: '14px',
                        fontWeight: 'bold',
                        display: 'inline-flex',
                        alignSelf: 'center', // Align with the button vertically
                        height: '38px', // Match button height for alignment
                        lineHeight: '28px' // Adjust line height for vertical centering
                    }}
                >
                    {alreadySelectedCount} already selected (highlighted in green)
                </span>
            )}
        </div>
        </div>
    </div>
  );
}

export default PasteIdInput;